<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import { CONSENT_REQUEST_TYPE } from "@core/constants"
import type { ConsentSentModalPayload } from "./consent-request-send-dialog.types"

// ================================
// INTERNATIONALIZATION
// ================================

const { t } = useI18n({
    messages: {
        fr: {
            change_owner_relance_dialog: "Relance — changement de titulaire",
            invitation_dialog_sign: "Envoi — signature",
            invitation_dialog_extend: "Envoi — prolongement",
            invitation_dialog_share: "Envoi — partage de données",
        },
    },
})

// ================================
// PROPS & EMITS
// ================================

const props = defineProps<{
    payload: ConsentSentModalPayload | null
}>()

const emit = defineEmits<{
    sent: []
    hide: []
}>()

const visible = defineModel<boolean>("visible", { default: false })

// ================================
// COMPUTED
// ================================

/**
 * Titre de la modale selon le type d’invitation (relance / envoi).
 */
const getDialogHeader = computed(() => {
    const p = props.payload
    if (!p) return ""
    if (p.kind === "change-owner") return t("change_owner_relance_dialog")
    if (p.kind === "share") return t("invitation_dialog_share")
    if (p.consentRequestType === CONSENT_REQUEST_TYPE.EXTEND) {
        return t("invitation_dialog_extend")
    }
    return t("invitation_dialog_sign")
})

// ================================
// INTERACTIONS UTILISATEUR
// ================================

/**
 * Fermeture de la modale (crois, overlay) : le parent réinitialise la charge utile.
 */
function onHide(): void {
    emit("hide")
}

/**
 * E-mail envoyé depuis le formulaire interne : le parent rafraîchit la demande / la source.
 */
function onSent(): void {
    emit("sent")
}

/**
 * Flux terminé côté formulaire interne : fermeture de la modale.
 */
function onDone(): void {
    visible.value = false
}
</script>

<template>
    <!-- ================================ -->
    <!-- Modale envoi / relance titulaire -->
    <!-- ================================ -->
    <BaseDialog
        v-model:visible="visible"
        class="consent_request_send_dialog"
        modal
        :closable="true"
        position="top"
        :header="getDialogHeader"
        :style="{ width: '50vw' }"
        :breakpoints="{ '1199px': '75vw', '575px': '90vw' }"
        :pt="{
            content:
                'max-h-[calc(100dvh-10rem)] overflow-y-auto overflow-x-hidden flex justify-center items-start w-full py-2',
        }"
        @hide="onHide"
    >
        <SourceInvitationChangeOwnerSent
            v-if="payload?.kind === 'change-owner'"
            class="w-full max-w-3xl shrink-0 py-2"
            :consent-request-id="payload.requestId"
            :link="payload.link"
            :send-back="payload.sendBack"
            @sent="onSent"
            @done="onDone"
        />
        <SourceInvitationShareSent
            v-else-if="payload?.kind === 'share'"
            class="w-full max-w-3xl shrink-0 py-2"
            :consent-request-id="payload.requestId"
            :consent-request-type="CONSENT_REQUEST_TYPE.SHARE"
            :link="payload.link"
            :send-back="payload.sendBack"
            :multiple="payload.multiple"
            @sent="onSent"
            @done="onDone"
        />
        <SourceInvitationSignSent
            v-else-if="payload?.kind === 'sign'"
            class="w-full max-w-3xl shrink-0 py-2"
            :consent-request-id="payload.requestId"
            :consent-request-type="payload.consentRequestType"
            :link="payload.link"
            :send-back="payload.sendBack"
            :multiple="payload.multiple"
            @sent="onSent"
            @done="onDone"
        />
    </BaseDialog>
</template>
